import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import { IS_FARMERSPULSE, fpPath } from "@/lib/site";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import FarmersNav from "@/components/FarmersNav";
import FarmersIntroCard from "@/components/FarmersIntroCard";
import FarmersWebsitePreview from "@/components/FarmersWebsitePreview";
import FarmersHomeHero from "@/farmers_pulse/standalone/HomeHero";
import GuideHelperChip from "@/components/GuideHelperChip";
import WhatDoYouNeed from "@/components/WhatDoYouNeed";
import SEOHead from "@/components/SEOHead";

// Maps each FarmersPulse dashboard module → the slug of the seeded CMS
// guide article that explains what the module does. Rendered as a small
// "How this works" chip that opens the article in a slide-over drawer.
const GUIDE_SLUG_FOR_MODULE = {
  "weather-today":       "weather-today",
  "rainfall":            "rainfall",
  "growing-conditions":  "growing-conditions",
  "farm-alerts":         "farm-alerts",
  "irrigation":          "irrigation",
  "what-to-plant":       "what-to-plant",
  "what-to-harvest":     "what-to-harvest",
  "farm-calendar":       "farm-calendar",
  "pest-disease-alerts": "pest-disease-alerts",
  "nearby-reports":      "nearby-farmer-reports",
  "my-farm":             "my-farm",
  "farm-diary":          "farm-diary",
  "agri-news":           "agricultural-news",
  "ask-ai":              "ask-farmers-pulse",
};
import {
  Sun, CloudRain, Plant, Warning, PottedPlant, Leaf, Drop, Bug, UsersThree, House,
  Notebook, Calendar, Newspaper, Sparkle, MapPin, ArrowRight, Plus, Books, Storefront,
} from "@phosphor-icons/react";

/**
 * FarmersPulseHome — the modular landing at /farmers.
 * All cards are rendered from the backend module registry
 * (`GET /api/farmers/modules`) so admins can toggle, reorder, or scope
 * them by geography without touching this file.
 */

const ICON_MAP = {
  sun: Sun, "cloud-rain": CloudRain, plant: Plant, warning: Warning,
  seed: PottedPlant, leaf: Leaf, drop: Drop, bug: Bug, users: UsersThree, house: House,
  notebook: Notebook, calendar: Calendar, newspaper: Newspaper, sparkle: Sparkle,
};

export default function FarmersPulseHome() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  // Bind to the shared FarmersPulse header location store
  // (`np:user_location`) so any change in the header pill / breadcrumb
  // instantly updates the module cards, calendar and nearby feed.
  const [loc, setLoc] = useState(() => {
    const shared = readLocation();
    if (shared?.lat && shared?.lon) return { lat: shared.lat, lon: shared.lon, label: shared.city || shared.country };
    try {
      const raw = localStorage.getItem("np:pulse-location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  useEffect(() => {
    const on = () => {
      const s = readLocation();
      if (s?.lat && s?.lon) {
        setLoc({ lat: s.lat, lon: s.lon, label: s.city || s.country });
      } else {
        // Clear stale loc so cards never contradict the header (iter-45 fix)
        setLoc(s?.country || s?.city ? { label: s.city || s.country } : null);
      }
    };
    window.addEventListener("np:location-changed", on);
    return () => window.removeEventListener("np:location-changed", on);
  }, []);
  const [nearby, setNearby] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [myFarms, setMyFarms] = useState([]);

  const isStandalone = process.env.REACT_APP_FARMERSPULSE_STANDALONE === "true";

  // Load modules once.
  useEffect(() => {
    http.get("/farmers/modules")
      .then(({ data }) => setModules(data.modules || []))
      .catch(() => toast.error("Could not load Farmers Pulse modules"))
      .finally(() => setLoading(false));
  }, []);

  // Load calendar + nearby whenever we have coordinates.
  useEffect(() => {
    if (!loc?.lat || !loc?.lon) return;
    http.get(`/farmers/calendar?latitude=${loc.lat}&longitude=${loc.lon}`)
      .then(({ data }) => setCalendar(data)).catch(() => {});
    http.get(`/farmers/observations/nearby?latitude=${loc.lat}&longitude=${loc.lon}&radius_km=50&limit=12`)
      .then(({ data }) => setNearby(data.observations || [])).catch(() => {});
  }, [loc?.lat, loc?.lon]);

  // Load my farms if logged in.
  useEffect(() => {
    if (!user) return;
    http.get("/farmers/farms/mine")
      .then(({ data }) => setMyFarms(data.farms || []))
      .catch(() => {});
  }, [user]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* noop */ }
    }, () => toast.error("Could not read your location. You can still browse."));
  };

  const enabledByKey = useMemo(() => {
    const map = {};
    for (const m of modules) map[m.key] = m;
    return map;
  }, [modules]);

  const heroTxt = "Farmers Pulse — a living almanac of what is happening on farms around the world. Local weather, crop calendars, pests and community observations, all in one place.";

  // Show a "submitted — pending review" banner when we come back from
  // /farmers/report?submitted=1 (moderation queue means the report isn't
  // visible on the feed yet).
  const showSubmittedBanner = typeof window !== "undefined" && window.location.search.includes("submitted=1");

  return (
    <main
      className={`bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] min-h-screen ${
        isStandalone ? "pt-0" : "pt-32 md:pt-36"
      } pb-32`}
      data-testid="farmers-pulse-home"
    >
      <SEOHead
        title="FarmersPulse™ — Weather, Crops, Markets & Farm Intelligence"
        description="FarmersPulse is a Living Agricultural Intelligence Network for farmers worldwide. Track weather, rainfall, crops, pests, planting windows, harvests, markets and nearby farmer reports — all personalized to your farm."
        path="/"
        image="https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2000&q=80"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "FarmersPulse",
          "url": "https://farmerspulse.com/",
          "description": "Living Agricultural Intelligence Network — weather, crops, pests, planting, harvest, markets, inventory, community.",
          "publisher": { "@type": "Organization", "name": "FarmersPulse" },
        }}
      />
      {/* New hero — Rhythm of Modern Farming — top of the page. */}
      <FarmersHomeHero />

      {/* "Doing not reading" — 12-tile action grid right below the hero */}
      <WhatDoYouNeed />

      {/* Meet the 12-tool toolkit — first-time discovery strip */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8"
        data-testid="home-toolkit-strip"
      >
        <div className="grid md:grid-cols-[1fr_auto] gap-4 md:gap-8 items-center rounded-3xl bg-[#151312] text-[#F5EBDC] p-6 md:p-8">
          <Link
            to={fpPath("/toolkit")}
            className="group flex flex-col hover:opacity-95 transition"
            data-testid="home-toolkit-strip-cta"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Farmer Toolkit · 24 tools · Farm Management + Marketing Suite
            </div>
            <h2 className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight">
              Grow it. Manage it. Sell it. Share it.
            </h2>
            <p className="mt-2 max-w-2xl text-[#E7DDCA]">
              Twelve farm-management tools that run the farm, and a twelve-tool Marketing Suite that finds customers, promotes harvests, and brings them back — every tool with a preview screen so you know what it does before you tap in.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase group-hover:opacity-90 self-start">
              Meet the toolkit <ArrowRight size={11} weight="bold" />
            </span>
          </Link>

          {/* Compact infographic — click to open full-size */}
          <a
            href="https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/tyxcakra_farmerspulse-tools-infographic.png"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-toolkit-infographic"
            className="group block shrink-0 rounded-2xl overflow-hidden border border-white/15 bg-white/5 hover:border-[color:var(--np-brand,#B85F2A)] transition self-stretch md:self-center"
            title="Open the full FarmersPulse toolkit infographic"
          >
            <img
              src="https://customer-assets-cm19k8pv.emergentagent.net/job_53d4d8d8-0828-4dbf-a7c6-bb2f75105ed3/artifacts/tyxcakra_farmerspulse-tools-infographic.png"
              alt="FarmersPulse — 24-tool digital farmer toolkit at a glance"
              className="block w-full md:w-[220px] lg:w-[260px] h-auto object-cover group-hover:scale-[1.02] transition"
              loading="lazy"
            />
            <div className="px-3 py-2 flex items-center justify-between gap-2 bg-white/5 border-t border-white/10">
              <span className="font-mono-tech text-[9px] tracking-[0.26em] uppercase text-[#E7DDCA]">
                View full infographic
              </span>
              <ArrowRight size={11} weight="bold" className="text-[color:var(--np-brand,#B85F2A)]" />
            </div>
          </a>
        </div>
      </section>

      {!isStandalone && <FarmersNav />}
      {/* Living Almanac intro — editorial block below the hero */}
      <section
        className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14"
        data-testid="farmers-almanac-intro"
      >
        <div className="rounded-3xl border border-black/10 bg-white/70 backdrop-blur px-6 md:px-10 py-8 md:py-10">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
            <div className="md:sticky md:top-40 space-y-1.5">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1.5">
                <Books size={12} weight="fill" /> Farmers Pulse
              </div>
              <div className="font-serif-display text-3xl md:text-4xl leading-[1.02] tracking-tight max-w-xs">
                A Modern Global Almanac for Farmers
              </div>
            </div>
            <div className="text-[#241209]">
              <p className="text-base md:text-lg leading-relaxed">
                For thousands of years, farmers have looked to the sky, the soil, plants, birds, insects, seasons, and generations of accumulated knowledge to decide when to plant, when to irrigate, when to harvest, and when to prepare for changing conditions.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed">
                Traditional farmers' almanacs helped organize much of this knowledge around seasons, weather, planting cycles, the sun, the moon, and the calendar. At <strong>NaturePulse.net</strong>, we want to take that idea much further.
              </p>
              <p className="mt-4 text-base md:text-lg leading-relaxed">
                We are introducing <strong>Farmers Pulse</strong> — a growing global agricultural information, observation, and knowledge platform that combines the wisdom of traditional farming with modern observations, local reporting, technology, weather information, crop knowledge, community participation, and eventually AI-powered analysis.
              </p>
              <p className="mt-4 font-serif-display text-xl md:text-2xl italic text-[#151312]">
                Observe better. Prepare earlier. Share knowledge. Reduce risk. Make better decisions.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Link
                  to={IS_FARMERSPULSE ? "/almanac" : "/farmers/almanac"}
                  data-testid="farmers-intro-read-more"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                >
                  Read the full story <ArrowRight size={11} weight="bold" />
                </Link>
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                  12 min read · Observe · Learn · Share · Grow
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick action bar — surface CTAs + location, works after the marketing hero */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10">
        {showSubmittedBanner && (
          <div
            data-testid="farmers-submitted-banner"
            className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 flex items-start gap-3"
          >
            <Sparkle size={18} weight="fill" className="mt-0.5" />
            <div className="flex-1">
              <div className="font-serif-display text-lg leading-tight">Report submitted — thank you.</div>
              <div className="mt-0.5 text-xs">
                It's queued for community review. Once verified, it will appear on the map and nearby feed.
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <ListenTranslateBar text={heroTxt} testidPrefix="farmers-hero-tools" />
          <ShareButton
            title="Farmers Pulse — NaturePulse"
            text="Local weather · crop calendars · community observations from farmers everywhere."
            hashtags={["FarmersPulse", "NaturePulse"]}
            variant="chip"
            testid="farmers-hero-share"
          />
          <Link
            to={fpPath("/report")}
            data-testid="farmers-hero-report"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> Report what you see
          </Link>
          <Link
            to={fpPath("/farms/new")}
            data-testid="farmers-hero-add-farm"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <House size={11} weight="fill" /> Add a farm
          </Link>
          <Link
            to={fpPath("/websites")}
            data-testid="farmers-hero-build-website"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Storefront size={11} weight="fill" /> Build a website
          </Link>
        </div>

        {/* Location strip */}
        <div className="mt-4 flex items-center gap-3 flex-wrap rounded-full border border-black/10 bg-white/70 backdrop-blur px-4 py-2">
          <MapPin size={14} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          <span className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-[#3A3830]">
            {loc?.label ? loc.label : "Location not set"}
            {loc?.lat && <span className="ml-2 text-[#3A3830]">{loc.lat.toFixed(2)}, {loc.lon.toFixed(2)}</span>}
          </span>
          <button
            onClick={useMyLocation}
            data-testid="farmers-use-location"
            className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
          >
            {loc ? "Update location" : "Use my location"}
          </button>
        </div>
      </section>

      {/* Live preview of the farmer's website(s) — or CTA to build one. */}
      <FarmersWebsitePreview />

      {/* Modular grid */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10" data-testid="farmers-modules">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-2xl border border-black/10 bg-white/60 h-48 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {modules.map((m) => (
              <ModuleCard
                key={m.id}
                module={m}
                loc={loc}
                calendar={calendar}
                nearby={nearby}
                myFarms={myFarms}
                signedIn={!!user}
              />
            ))}
          </div>
        )}
      </section>

      {/* Intro article beneath modules */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <FarmersIntroCard />
      </section>

      {/* Footer strip */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-12">
        <div className="rounded-2xl border border-black/10 bg-white p-5 flex flex-wrap items-center gap-4">
          <Books size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Explore</div>
            <div className="mt-0.5 font-serif-display text-lg">Crop database, calendars and community.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={fpPath("/crops")} data-testid="farmers-explore-crops" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              Crop database
            </Link>
            <Link to={fpPath("/calendar")} data-testid="farmers-explore-calendar" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              Planting calendar
            </Link>
            <Link to={fpPath("/pest-heatmap")} data-testid="farmers-explore-heatmap" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              Pest heatmap
            </Link>
            <Link to={fpPath("/farms")} data-testid="farmers-explore-my-farms" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              My farms
            </Link>
            <Link to={fpPath("/inventory")} data-testid="farmers-explore-inventory" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              Inventory
            </Link>
            <Link to={fpPath("/websites")} data-testid="farmers-explore-websites" className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
              Build a website
            </Link>
            <Link to={fpPath("/report")} data-testid="farmers-explore-report" className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
              Report what you see
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * Renders one card based on its module key. The switch keeps the layout
 * for each card local — every module is independent and can be toggled
 * off in the admin without breaking the grid.
 */
function ModuleCard({ module, loc, calendar, nearby, myFarms, signedIn }) {
  const Icon = ICON_MAP[module.icon] || Sparkle;
  const shell = "rounded-2xl border border-black/10 bg-white p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition h-full flex flex-col";
  const guideSlug = GUIDE_SLUG_FOR_MODULE[module.key];
  const meta = (
    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5 flex-wrap">
      <Icon size={12} weight="fill" /> {module.name}
      {guideSlug && <GuideHelperChip slug={guideSlug} />}
    </div>
  );

  switch (module.key) {
    case "weather-today":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-3xl leading-tight">Today's sky</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Local temperature, humidity, wind and rainfall. Uses the NaturePulse Pulse dashboard as the underlying source.
          </p>
          <Link
            to="/pulse"
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            Open weather Pulse <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "rainfall":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Rainfall watch</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Today, this week and this season rainfall totals — with historical baselines so you can spot dry spells early.
          </p>
          <Link to="/pulse" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open rain Pulse <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "growing-conditions":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Growing conditions</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Weather translated into farmer guidance — soil workability, transplant risk, spray windows. Marked as AI interpretation, never as verified fact.
          </p>
          <Link to="/pulse" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            See conditions <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "farm-alerts":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Farm alerts</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Frost, drought, heat, storm and pest early-warnings for your region — pulled from NaturePulse observations and weather feeds.
          </p>
          <Link to="/notifications" data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open alerts <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "irrigation":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Irrigation</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            Suggested irrigation windows based on rainfall received and short-term evapotranspiration for your area.
          </p>
          <Link to={fpPath("/farms")} data-testid={`farmers-card-${module.key}-cta`} className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Set up on my farm <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "farm-calendar":
    case "what-to-plant":
    case "what-to-harvest": {
      const key = module.key;
      const filterKind = key === "what-to-plant" ? "sow" : key === "what-to-harvest" ? "harvest" : null;
      const items = (calendar?.crops || []).filter((c) => !filterKind || c.what_to_do_now === filterKind).slice(0, 6);
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">
            {key === "what-to-plant" ? "Sow this week" : key === "what-to-harvest" ? "Ready to harvest" : "Your crop calendar"}
          </div>
          {!loc?.lat && (
            <p className="mt-2 text-sm text-[#3A3830]">Set your location above to see a local calendar.</p>
          )}
          {loc?.lat && !calendar && (
            <p className="mt-2 text-sm text-[#3A3830]">Loading calendar…</p>
          )}
          {loc?.lat && calendar && items.length === 0 && (
            <p className="mt-2 text-sm text-[#3A3830]">
              No {key === "what-to-plant" ? "planting" : "harvest"} windows for {calendar?.climate_zone || "your region"} this month.
            </p>
          )}
          {items.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {items.map((c) => (
                <li key={c.slug} className="flex items-center gap-2">
                  <span className="text-lg">{c.emoji || "🌱"}</span>
                  <Link to={fpPath(`/crops/${c.slug}`)} className="font-serif-display text-base hover:text-[color:var(--np-brand,#B85F2A)]">
                    {c.common_name}
                  </Link>
                  <span className="ml-auto rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                    {c.what_to_do_now}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {calendar && (
            <div className="mt-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
              {calendar.climate_zone} · {calendar.hemisphere} hemisphere · month {calendar.current_month}
            </div>
          )}
          <Link to={fpPath("/calendar")} className="mt-auto pt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Full calendar <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );
    }

    case "nearby-reports":
    case "pest-disease-alerts": {
      const filterCat = module.key === "pest-disease-alerts" ? ["pest", "disease"] : null;
      const items = (nearby || []).filter((n) => {
        if (!filterCat) return true;
        return filterCat.includes(n.category) || (n.labels || []).some((l) => filterCat.includes(l));
      }).slice(0, 5);
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">
            {module.key === "pest-disease-alerts" ? "Pest & disease pressure near you" : "What's happening around you"}
          </div>
          {!loc?.lat && <p className="mt-2 text-sm text-[#3A3830]">Set your location above to see nearby reports.</p>}
          {loc?.lat && items.length === 0 && <p className="mt-2 text-sm text-[#3A3830]">No reports within 50 km yet — be the first.</p>}
          {items.map((n) => (
            <Link
              key={n.id}
              to={`/observations/${n.id}`}
              data-testid={`farmers-nearby-${n.id}`}
              className="mt-3 rounded-lg border border-black/10 p-3 hover:border-[color:var(--np-brand,#B85F2A)] transition block"
            >
              <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {n.category} · {n.km} km
                {n.severity && <><span className="text-[#3A3830]"> · </span>{n.severity}</>}
              </div>
              <div className="mt-1 font-serif-display text-base leading-tight line-clamp-2">{n.title || "Untitled report"}</div>
              {n.description && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{n.description}</div>}
            </Link>
          ))}
          <Link
            to={module.key === "pest-disease-alerts" ? fpPath("/pest-heatmap") : fpPath("/report")}
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            {module.key === "pest-disease-alerts" ? "Open pest heatmap" : "Add a report"} <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );
    }

    case "my-farm":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Your farms</div>
          {!signedIn ? (
            <p className="mt-2 text-sm text-[#3A3830]">Sign in to create and manage private or public farm records.</p>
          ) : myFarms.length === 0 ? (
            <p className="mt-2 text-sm text-[#3A3830]">You haven't added a farm yet. Farms unlock your diary and personalized recommendations.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {myFarms.slice(0, 3).map((f) => (
                <li key={f.id}>
                  <Link to={fpPath(`/farms/${f.id}`)} className="rounded-lg border border-black/10 p-3 block hover:border-[color:var(--np-brand,#B85F2A)]">
                    <div className="font-serif-display text-base leading-tight">{f.name}</div>
                    <div className="mt-0.5 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[#3A3830]">
                      {[f.village, f.district, f.state, f.country].filter(Boolean).join(", ") || "Location not set"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto pt-3 flex gap-2">
            <Link to={fpPath("/farms")} className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">All farms</Link>
            <Link to={fpPath("/farms/new")} className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">+ New farm</Link>
          </div>
        </div>
      );

    case "farm-diary":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Your farm diary</div>
          <p className="mt-2 text-sm text-[#3A3830]">
            A daily journal for planting, weather, pests, pollinators and harvests. Records stay private by default.
          </p>
          <Link
            to={myFarms[0] ? fpPath(`/farms/${myFarms[0].id}`) : fpPath("/farms")}
            data-testid={`farmers-card-${module.key}-cta`}
            className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            {myFarms[0] ? "Open my diary" : "Set up your first farm"} <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "agri-news":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Agricultural news</div>
          <p className="mt-2 text-sm text-[#3A3830]">Curated feeds on agriculture, plant science, water and climate change — with original sources preserved.</p>
          <Link to="/news?category=agriculture" className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Open news <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      );

    case "ask-ai":
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">Ask Farmers Pulse</div>
          <p className="mt-2 text-sm text-[#3A3830]">Ask questions grounded on your farm, local weather and nearby observations. Coming in Phase 2.</p>
          <span className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
            <Sparkle size={10} weight="fill" /> Preview
          </span>
        </div>
      );

    default:
      return (
        <div className={shell} data-testid={`farmers-card-${module.key}`}>
          {meta}
          <div className="mt-2 font-serif-display text-2xl leading-tight">{module.name}</div>
          {module.description && <p className="mt-2 text-sm text-[#3A3830]">{module.description}</p>}
          <span className="mt-auto pt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
            Enabled
          </span>
        </div>
      );
  }
}
